/**
 * One level, as a slider and a number.
 *
 * A NATIVE `input[type=range]` with `accent-color`, which is the shape every
 * other slider in this CRM already has (the branding panel, the grid zoom). A
 * hand-built track would be four pseudo-element rules per browser for something
 * the platform draws correctly, including the keyboard behaviour: arrow keys
 * step, Home and End jump to the ends, and none of that survives being rebuilt
 * out of divs.
 *
 * The number beside it is a fixed width on purpose. It reads "5%" and "200%" in
 * the same place, so the track does not change length as the thumb is dragged -
 * inside a dropdown anchored to a button, a control that resizes under the
 * pointer is the panel re-anchoring itself mid-gesture.
 */
import { useT } from "../../../i18n/I18nProvider";
import { volumeToPercent, percentToVolume } from "./volume";

/**
 * Whole percent per keypress and per notch.
 *
 * Five rather than one because 100 has to be reachable by dragging: it is the
 * value an operator returns to, and a step that does not divide it evenly means
 * the only way back to exactly unity is the keyboard.
 */
const STEP = 5;

export function VolumeSlider({
  value,
  max,
  label,
  onChange,
}: {
  /** The multiplier, where 1 is unity. */
  value: number;
  /** This path's ceiling as a multiplier - see `AUDIO_VOLUME_MAX`. */
  max: number;
  /** The name of the path this turns, for the screen reader. */
  label: string;
  onChange: (value: number) => void;
}) {
  const t = useT();
  const percent = volumeToPercent(value);
  return (
    <div className="crm-vol">
      <input
        type="range"
        min={0}
        max={volumeToPercent(max)}
        step={STEP}
        value={percent}
        aria-label={t("calls.audio.level", { what: label })}
        onChange={(e) => onChange(percentToVolume(Number(e.target.value)))}
      />
      {/* Muted is worth saying in words. A slider parked at the far left is a
          shape somebody has to look twice at; "0%" beside it is not. */}
      <span className={percent === 0 ? "crm-vol-n is-off" : "crm-vol-n"}>
        {percent === 0 ? t("calls.audio.muted") : `${percent}%`}
      </span>
    </div>
  );
}
